import React from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    BooleanInput,
    SelectInput,
    required,
    minLength,
    regex,
    Toolbar,
    SaveButton,
    DeleteButton,
    useRedirect,
    useTranslate,
    useNotify,
    DateField,
    FunctionField,
} from 'react-admin';
import { AppUserGroupsField } from './AppUserGroupsField';
import { Box, Typography, Alert, Card, CardContent, IconButton, Divider, Chip } from '@mui/material';
import {
    Person,
    DirectionsCar,
    Settings,
    ArrowBack,
    Phone,
    NotificationsActive,
    AccountBalanceWallet,
    History,
    VerifiedUser
} from '@mui/icons-material';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const APP_USER_KNOWN_FIELDS = new Set([
    'email', 'phone', 'firstName', 'lastName', 'displayName', 'language',
    'vehicleMake', 'vehicleModel', 'carPlate', 'isActive', 'isVerified',
    'notificationPreferences', 'referralCode', 'password',
]);

const mapAppUserBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const appUserFieldLabelKey = (field: string): string => {
    const head = field.split('.')[0];
    return APP_USER_KNOWN_FIELDS.has(head)
        ? `resources.app-users.fields.${head}`
        : field;
};

const EditToolbar = () => (
    <Toolbar
        sx={{
            backgroundColor: '#FAFAFA',
            borderTop: '1px solid #E0E0E0',
            px: { xs: 2, sm: 3 },
            py: 2,
            mt: { xs: 2, sm: 4 },
            display: 'flex',
            justifyContent: 'space-between',
            '& .MuiButton-root': {
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
            },
        }}
    >
        <SaveButton
            variant="contained"
            sx={{
                backgroundColor: '#00C853',
                '&:hover': {
                    backgroundColor: '#00A843',
                    boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                },
            }}
        />
        <DeleteButton
            sx={{
                color: '#F44336',
                '&:hover': {
                    backgroundColor: 'rgba(244, 67, 54, 0.08)',
                },
            }}
        />
    </Toolbar>
);

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/app-users')}
                size="large"
                sx={{
                    flexShrink: 0,
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('resources.app-users.edit.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2.125rem' }, mb: 0.5 }}>
                    {translate('resources.app-users.edit.title')}
                </Typography>
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                        fontSize: { xs: '0.75rem', sm: '1rem' },
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                    }}
                >
                    {translate('resources.app-users.edit.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

export const AppUserEdit = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});
    const validatePhone = [
        required(),
        regex(/^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/, translate('resources.app-users.create.phoneValidation.format'))
    ];

    return (
        <Edit
            title={false}
            actions={false}
            component="div"
            mutationMode="pessimistic"
            redirect="list"
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.app-users.notifications.updateSuccess', {
                        type: 'success',
                        messageArgs: { _: 'App user updated successfully' },
                    });
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapAppUserBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(appUserFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);
                    const headerKey = apiError?.errorCode ? `errors.${apiError.errorCode}` : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });
                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, { type: 'error', multiLine: true, autoHideDuration: 10000 });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                backgroundColor: 'transparent',
                maxWidth: '100%',
                overflow: 'hidden',
                '& .RaEdit-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<EditToolbar />}
                sx={{
                    '& .MuiPaper-root': { backgroundColor: 'transparent', boxShadow: 'none' },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />

                    {/* User Summary Card */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, backgroundColor: '#F8F8F8', overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1, sm: 0 } }}>
                                <Box>
                                    <FunctionField
                                        render={(record: any) => (
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: { xs: '1rem', sm: '1.25rem' },
                                                    mb: 1,
                                                    wordBreak: 'break-word',
                                                    overflowWrap: 'anywhere',
                                                }}
                                            >
                                                {record.name || `${record.firstName || ''} ${record.lastName || ''}`.trim() || record.phone}
                                            </Typography>
                                        )}
                                    />
                                    <FunctionField
                                        render={(record: any) => (
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
                                                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                                    ID: {record.id}
                                                </Typography>
                                                {record.email && (
                                                    <>
                                                        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{ minWidth: 0, wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                                                        >
                                                            {record.email}
                                                        </Typography>
                                                    </>
                                                )}
                                            </Box>
                                        )}
                                    />
                                </Box>
                                <FunctionField
                                    render={(record: any) => (
                                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {record.isVerified && (
                                                <Chip
                                                    icon={<VerifiedUser sx={{ fontSize: 16 }} />}
                                                    label={translate('resources.app-users.status.verified')}
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                />
                                            )}
                                            <Chip
                                                label={record.isActive ? translate('resources.app-users.status.active') : translate('resources.app-users.status.inactive')}
                                                size="small"
                                                color={record.isActive ? 'success' : 'default'}
                                                variant="filled"
                                            />
                                        </Box>
                                    )}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Basic Information Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Person sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.app-users.edit.basicInfo')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <TextInput
                                    source="email"
                                    type="email"
                                    fullWidth
                                    helperText={translate('resources.app-users.edit.emailHelperText')}
                                />

                                <TextInput
                                    source="phone"
                                    disabled
                                    fullWidth
                                    helperText={translate('resources.app-users.edit.phoneCannotEdit')}
                                    InputProps={{
                                        startAdornment: <Phone sx={{ mr: 1, color: '#666' }} />,
                                    }}
                                />

                                <TextInput
                                    source="firstName"
                                    fullWidth
                                />

                                <TextInput
                                    source="lastName"
                                    fullWidth
                                />

                                <SelectInput
                                    source="language"
                                    choices={[
                                        { id: 'en', name: translate('language.en') },
                                        { id: 'zh_TW', name: translate('language.zh_TW') },
                                    ]}
                                    fullWidth
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Vehicle Information Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <DirectionsCar sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.app-users.edit.vehicleInfo')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <SelectInput
                                    source="vehicleMake"
                                    choices={[
                                        { id: 'Tesla', name: 'Tesla' },
                                        { id: 'BYD', name: 'BYD' },
                                        { id: 'BMW', name: 'BMW' },
                                        { id: 'Mercedes-Benz', name: 'Mercedes-Benz' },
                                        { id: 'Audi', name: 'Audi' },
                                        { id: 'Volkswagen', name: 'Volkswagen' },
                                        { id: 'Nissan', name: 'Nissan' },
                                        { id: 'Hyundai', name: 'Hyundai' },
                                        { id: 'Kia', name: 'Kia' },
                                        { id: 'Toyota', name: 'Toyota' },
                                        { id: 'Other', name: translate('common.other') },
                                    ]}
                                    fullWidth
                                />

                                <TextInput
                                    source="vehicleModel"
                                    fullWidth
                                />

                                <TextInput
                                    source="carPlate"
                                    fullWidth
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Account Settings Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Settings sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.app-users.edit.accountSettings')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <BooleanInput
                                    source="isActive"
                                    label={translate('resources.app-users.edit.activeAccount')}
                                    sx={{
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '1rem',
                                        },
                                    }}
                                />

                                <BooleanInput
                                    source="isVerified"
                                    label={translate('resources.app-users.fields.verified')}
                                    sx={{
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '1rem',
                                        },
                                    }}
                                />

                                <Alert severity="warning" sx={{ mt: 2 }}>
                                    <Typography variant="body2">
                                        {translate('resources.app-users.edit.accountWarning')}
                                    </Typography>
                                </Alert>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Wallet Information Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <AccountBalanceWallet sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.app-users.edit.walletInfo')}
                                </Typography>
                            </Box>

                            <Box sx={{ p: 2, backgroundColor: '#F5F5F5', borderRadius: 2 }}>
                                <FunctionField
                                    render={(record: any) => (
                                        <Box>
                                            <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                                                {translate('resources.app-users.fields.walletBalance')}:
                                                <Box component="span" sx={{ ml: 2, color: '#00C853' }}>
                                                    {record.walletCurrency || 'HKD'} {(record.walletBalance || 0).toFixed(2)}
                                                </Box>
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {translate('resources.app-users.edit.walletReadOnly')}
                                            </Typography>
                                        </Box>
                                    )}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Notification Preferences Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <NotificationsActive sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.app-users.edit.notificationPreferences')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <BooleanInput
                                    source="notificationPreferences.chargingComplete"
                                    label={translate('resources.app-users.notifications.chargingComplete')}
                                    sx={{
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '0.95rem',
                                        },
                                    }}
                                />

                                <BooleanInput
                                    source="notificationPreferences.chargingInterrupted"
                                    label={translate('resources.app-users.notifications.chargingInterrupted')}
                                    sx={{
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '0.95rem',
                                        },
                                    }}
                                />

                                <BooleanInput
                                    source="notificationPreferences.promotions"
                                    label={translate('resources.app-users.notifications.promotions')}
                                    sx={{
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '0.95rem',
                                        },
                                    }}
                                />

                                <BooleanInput
                                    source="notificationPreferences.systemUpdates"
                                    label={translate('resources.app-users.notifications.systemUpdates')}
                                    sx={{
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '0.95rem',
                                        },
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Groups Section */}
                    <AppUserGroupsField />

                    {/* Activity Information Section */}
                    <Card sx={{ overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <History sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.app-users.edit.activityInfo')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gap: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: { xs: 0.5, sm: 0 } }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {translate('resources.app-users.fields.createdAt')}:
                                    </Typography>
                                    <DateField
                                        source="createdAt"
                                        showTime
                                        options={{
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        }}
                                    />
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: { xs: 0.5, sm: 0 } }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {translate('resources.app-users.fields.updatedAt')}:
                                    </Typography>
                                    <DateField
                                        source="updatedAt"
                                        showTime
                                        options={{
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        }}
                                    />
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: { xs: 0.5, sm: 0 } }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {translate('resources.app-users.fields.lastSeenAt')}:
                                    </Typography>
                                    <DateField
                                        source="lastSeenAt"
                                        showTime
                                        options={{
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        }}
                                    />
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: { xs: 0.5, sm: 0 } }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {translate('resources.app-users.fields.lastUpdatedBy')}:
                                    </Typography>
                                    <FunctionField
                                        render={(record: any) => (
                                            <Chip
                                                label={record.lastUpdatedBy || 'N/A'}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                    maxWidth: '100%',
                                                    '& .MuiChip-label': {
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Edit>
    );
};
